import { useState } from "react";

import { icons, Input } from "~/ui";
import type { ExistingUser } from "~/utils";

const ATHENA_NOTICE =
  "This is the login email for the hub. Changing it won't update the email stored in Athena or the email used for marketing communications.";

export interface EmailEditState {
  isEditing: boolean;
  canEdit: boolean;
  isSaving: boolean;
  error?: string;
  existingUser: ExistingUser | null;
  onOpenExistingUser: (userId: string) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (email: string) => void;
}

const EmailEditor = ({
  email,
  isSaving,
  error,
  existingUser,
  onOpenExistingUser,
  onCancel,
  onSave,
}: {
  email: string;
  isSaving: boolean;
  error?: string;
  existingUser: ExistingUser | null;
  onOpenExistingUser: (userId: string) => void;
  onCancel: () => void;
  onSave: (email: string) => void;
}) => {
  const [draft, setDraft] = useState(email);
  const [isErrorDismissed, setIsErrorDismissed] = useState(false);

  const canSubmit = !isSaving && draft.trim().length > 0;

  const submit = () => {
    if (!canSubmit) return;

    setIsErrorDismissed(false);
    onSave(draft.trim());
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Input
          id="user-email"
          inputType="email"
          aria-label="Hub email"
          placeholder="Enter email"
          value={draft}
          disabled={isSaving}
          error={isErrorDismissed ? undefined : error}
          containerClassName="flex-1"
          onChange={(event) => {
            setDraft(event.target.value);
            setIsErrorDismissed(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submit();
            }

            if (event.key === "Escape") {
              event.preventDefault();
              onCancel();
            }
          }}
        />
        <button
          type="button"
          title="Confirm"
          aria-label="Confirm email"
          className="text-indigo disabled:opacity-40"
          disabled={!canSubmit}
          onClick={submit}
        >
          <icons.outline.CheckCircleIcon className="size-7 cursor-pointer" />
        </button>
        <button
          type="button"
          title="Cancel"
          aria-label="Cancel email edit"
          className="text-indigo disabled:opacity-40"
          disabled={isSaving}
          onClick={onCancel}
        >
          <icons.outline.XCircleIcon className="size-7 cursor-pointer" />
        </button>
      </div>
      {existingUser && !isErrorDismissed && (
        <button
          type="button"
          className="text-violet self-start text-sm underline"
          onClick={() => onOpenExistingUser(existingUser.id)}
        >
          {`Open ${existingUser.firstName} ${existingUser.lastName} in User Details`}
        </button>
      )}
      <div className="flex gap-1.5 text-sm text-neutral-600">
        <icons.outline.ExclamationCircleIcon className="mt-0.5 size-4 shrink-0" />
        <p>{ATHENA_NOTICE}</p>
      </div>
    </div>
  );
};

export const EditableEmailRow = ({
  email,
  emailEdit,
}: {
  email: string;
  emailEdit?: EmailEditState;
}) => {
  if (emailEdit?.isEditing) {
    return (
      <EmailEditor
        email={email}
        isSaving={emailEdit.isSaving}
        error={emailEdit.error}
        existingUser={emailEdit.existingUser}
        onOpenExistingUser={emailEdit.onOpenExistingUser}
        onCancel={emailEdit.onCancel}
        onSave={emailEdit.onSave}
      />
    );
  }

  return (
    <div className="flex items-center gap-3">
      <span className="min-w-0 break-all">{email}</span>
      {emailEdit?.canEdit && (
        <button
          type="button"
          title="Edit email"
          aria-label="Edit email"
          className="shrink-0"
          onClick={emailEdit.onEdit}
        >
          <icons.outline.PencilIcon className="text-indigo size-5 cursor-pointer" />
        </button>
      )}
    </div>
  );
};
